import type { IntakeResumeParseResponse } from "../types/api";
import { optionalEnglishLevel, optionalGender } from "../lib/candidateForm";
import { optionalLakhsToPaise } from "../lib/ctcLakhs";
import { validateOptionalEmail } from "../lib/email";
import { toPhoneForApi, validateIndianPhoneField } from "../lib/phone";

export type BulkPhase = "drop" | "staging" | "processing" | "reviewing" | "submitting" | "done";

export type BulkItemStatus = "pending" | "uploading" | "parsing" | "done" | "error";

export interface BulkItem {
  id: string;
  fileName: string;
  fileRef: File | null;
  status: BulkItemStatus;
  errorMessage: string | null;
  resumeId: string | null;
  parsedName: string;
  parsedPhone: string;
  parsedEmail: string;
  parsedTitle: string;
  parsedExperience: number | null;
  parsedCities: string[];
  parsedSkills: string[];
  parsedGender: string;
  parsedSegments: string[];
  parsedRoleFamily: string;
  parsedCurrentCtcLakhs: number | null;
  parsedExpectedCtcLakhs: number | null;
  parsedRaw: IntakeResumeParseResponse | null;
  selectedForSubmit: boolean;
  callNotes: string;
  candidateId: string | null;
  submissionId: string | null;
  submitError: string | null;
}

export const BULK_PARALLEL = 3;
export const BULK_SAVE_PARALLEL = 5;
export const BULK_MAX_FILES = 25;
export const BULK_GENDER_REQUIRED = "Select a gender";
export const BULK_SEGMENT_REQUIRED = "Select at least one segment";
export const BULK_SAVE_NEEDS_FIELDS = "Add a valid name, phone, gender, and segment first";
export const BULK_SET_SEGMENTS_LABEL = "Set segments for all unsaved";
export const BULK_SET_SEGMENTS_APPLY = "Apply";
export const BULK_SUGGESTED_SEGMENT_LIMIT = 2;
export const BULK_MAX_MB = 5;
export const BULK_SESSION_KEY = "axiohire:bulk-session";
export const BULK_SESSION_TTL_MS = 4 * 60 * 60 * 1000;
export const BYTES_PER_KILOBYTE = 1024;
export const BULK_ACCEPT =
  ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export interface BulkSessionPayload {
  items: Omit<BulkItem, "fileRef">[];
  savedAt: number;
}

export function createBulkItem(file: File): BulkItem {
  return {
    id: crypto.randomUUID(),
    fileName: file.name,
    fileRef: file,
    status: "pending",
    errorMessage: null,
    resumeId: null,
    parsedName: "",
    parsedPhone: "",
    parsedEmail: "",
    parsedTitle: "",
    parsedExperience: null,
    parsedCities: [],
    parsedSkills: [],
    parsedGender: "",
    parsedSegments: [],
    parsedRoleFamily: "",
    parsedCurrentCtcLakhs: null,
    parsedExpectedCtcLakhs: null,
    parsedRaw: null,
    selectedForSubmit: false,
    callNotes: "",
    candidateId: null,
    submissionId: null,
    submitError: null,
  };
}

export function nameFromFileName(fileName: string): string {
  return fileName.replace(/\.(pdf|docx)$/i, "").replace(/[_-]+/g, " ").trim();
}

export function formatFileBytes(size: number): string {
  if (size < BYTES_PER_KILOBYTE) return `${size} B`;
  if (size < BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE) {
    return `${(size / BYTES_PER_KILOBYTE).toFixed(0)} KB`;
  }
  return `${(size / (BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE)).toFixed(1)} MB`;
}

export function serializeBulkItems(items: BulkItem[]): Omit<BulkItem, "fileRef">[] {
  return items.map(({ fileRef: _fileRef, ...rest }) => rest);
}

export function isBusyStatus(status: BulkItemStatus): boolean {
  return status === "pending" || status === "uploading" || status === "parsing";
}

export function validateBulkName(name: string): string | null {
  return name.trim().length >= 2 ? null : "Name must be at least 2 characters";
}

export function validateBulkGender(gender: string): string | null {
  return gender.trim() ? null : BULK_GENDER_REQUIRED;
}

export function validateBulkSegments(segments: string[]): string | null {
  return segments.length > 0 ? null : BULK_SEGMENT_REQUIRED;
}

export function isBulkItemSaveable(item: BulkItem): boolean {
  return validateBulkName(item.parsedName) === null
    && validateIndianPhoneField(item.parsedPhone) === null
    && validateBulkGender(item.parsedGender) === null
    && validateBulkSegments(item.parsedSegments) === null;
}

export function chunkList<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export async function saveAllPending(
  items: BulkItem[],
  save: (id: string) => Promise<void>,
  after: () => void,
): Promise<void> {
  const pending = items.filter(
    (item) => item.status === "done" && item.candidateId === null && isBulkItemSaveable(item),
  );
  await runInChunks(pending, BULK_SAVE_PARALLEL, (item) => save(item.id));
  after();
}

export async function runInChunks<T>(
  items: T[],
  size: number,
  run: (item: T) => Promise<void>,
): Promise<void> {
  for (const chunk of chunkList(items, size)) {
    await Promise.allSettled(chunk.map((item) => run(item)));
  }
}

export function suggestedSegments(codes: string[] | undefined, known: Set<string> | null): string[] {
  const unique: string[] = [];
  for (const code of codes ?? []) {
    if (!code || unique.includes(code)) continue;
    if (known && known.size > 0 && !known.has(code)) continue;
    unique.push(code);
    if (unique.length >= BULK_SUGGESTED_SEGMENT_LIMIT) break;
  }
  return unique;
}

export function suggestedRoleFamily(code: string | null | undefined, known: Set<string> | null): string {
  if (!code) return "";
  if (known && known.size > 0 && !known.has(code)) return "";
  return code;
}

export function applySegmentsToEmpty(items: BulkItem[], segments: string[]): BulkItem[] {
  if (segments.length === 0) return items;
  return items.map((item) => {
    if (item.candidateId !== null || item.parsedSegments.length > 0) return item;
    return { ...item, parsedSegments: [...segments] };
  });
}

export function withBulkFieldDefaults<T extends Partial<BulkItem>>(item: T): T & Pick<
  BulkItem,
  "parsedGender" | "parsedSegments" | "parsedRoleFamily" | "parsedCurrentCtcLakhs" | "parsedExpectedCtcLakhs" | "selectedForSubmit"
> {
  return {
    ...item,
    selectedForSubmit: item.selectedForSubmit ?? false,
    parsedGender: item.parsedGender ?? "",
    parsedSegments: item.parsedSegments ?? [],
    parsedRoleFamily: item.parsedRoleFamily ?? "",
    parsedCurrentCtcLakhs: item.parsedCurrentCtcLakhs ?? null,
    parsedExpectedCtcLakhs: item.parsedExpectedCtcLakhs ?? null,
  };
}

export function selectedForSubmitSet(items: BulkItem[]): BulkItem[] {
  return items.filter((item) => item.candidateId !== null && item.selectedForSubmit);
}

export function mapParsedSkillCodes(
  skills: string[],
  codeByLabel: Map<string, string>,
): string[] {
  const seen = new Set<string>();
  const mapped: string[] = [];
  for (const skill of skills) {
    const code = codeByLabel.get(skill.trim().toLowerCase());
    if (!code || seen.has(code)) continue;
    seen.add(code);
    mapped.push(code);
  }
  return mapped;
}

function sameStringList(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export function remapBulkItemCatalogFields(
  item: BulkItem,
  resolveCity: ((raw: string | null | undefined) => string | null) | null,
  codeByLabel: Map<string, string> | null,
): BulkItem {
  if (item.status !== "done" || item.candidateId !== null) return item;

  let parsedCities = item.parsedCities;
  if (resolveCity) {
    parsedCities = [...new Set(
      item.parsedCities
        .map((city) => resolveCity(city))
        .filter((city): city is string => Boolean(city)),
    )];
  }

  let parsedSkills = item.parsedSkills;
  if (codeByLabel && codeByLabel.size > 0) {
    parsedSkills = mapParsedSkillCodes(item.parsedSkills, codeByLabel);
  }

  if (sameStringList(item.parsedCities, parsedCities) && sameStringList(item.parsedSkills, parsedSkills)) {
    return item;
  }
  return { ...item, parsedCities, parsedSkills };
}

const BULK_CURRENCY = "INR";
const BULK_WORK_STATUS_FRESHER = "fresher";
const BULK_WORK_STATUS_WORKING = "working";

export function payloadFromItem(item: BulkItem) {
  const parsed = item.parsedRaw;
  const email = item.parsedEmail.trim();
  const emailOk = validateOptionalEmail(email) === null;
  return {
    full_name: item.parsedName.trim(),
    phone: toPhoneForApi(item.parsedPhone),
    email: email && emailOk ? email : undefined,
    cities: item.parsedCities,
    segments: item.parsedSegments,
    skills: item.parsedSkills,
    languages: parsed?.languages ?? [],
    years_experience: item.parsedExperience ?? undefined,
    current_title: item.parsedTitle || undefined,
    education_summary: parsed?.education_summary || undefined,
    linkedin_url: parsed?.linkedin_url || undefined,
    notes: item.callNotes || undefined,
    consent_given: true,
    resume_id: item.resumeId ?? undefined,
    gender: optionalGender(item.parsedGender),
    role_family_code: item.parsedRoleFamily || undefined,
    work_status:
      parsed?.work_status === BULK_WORK_STATUS_FRESHER || item.parsedExperience === 0
        ? BULK_WORK_STATUS_FRESHER
        : BULK_WORK_STATUS_WORKING,
    education_level: parsed?.education_level || undefined,
    college_name: parsed?.college_name || undefined,
    degree: parsed?.degree || undefined,
    specialization: parsed?.specialization || undefined,
    graduation_year: parsed?.graduation_year || undefined,
    english_level: optionalEnglishLevel(parsed?.english_level),
    current_ctc_paise: optionalLakhsToPaise(item.parsedCurrentCtcLakhs),
    expected_ctc_paise: optionalLakhsToPaise(item.parsedExpectedCtcLakhs),
    availability_in_days: parsed?.availability_in_days ?? undefined,
    currency: BULK_CURRENCY,
    whatsapp_opt_in: false,
  } as const;
}
